import { useLocation } from "wouter";
import { useAdminProperty } from "@/hooks/use-admin-property";
import { isSiteWideAdminPath } from "@/lib/admin-scope";

/**
 * Admin screens whose data is not per-property.
 *
 * The switcher is rendered by AdminLayout, so it appeared on every admin screen
 * including these - where changing it rewrote the URL and changed nothing on the
 * page. A control that visibly does nothing reads as broken, and the bigger the
 * portfolio grows the more often someone will try it and wonder which of the two
 * is lying. These screens say what they are instead of offering a choice that
 * has no effect.
 *
 * Blog, backlinks, link opportunities and outreach are all properties of the
 * SITE rather than of a home: one domain, one article, one inbound link. Users
 * are staff accounts, not guests of a particular house.
 */
export function AdminPropertySwitcher() {
  const [location] = useLocation();
  const { properties, selectedSlug, setProperty, active, isAllProperties } = useAdminProperty();

  if (isSiteWideAdminPath(location)) {
    return (
      <p
        className="inline-flex items-center gap-2 rounded-full bg-sand/70 px-3 py-1 font-sans text-[12px] text-ink/80"
        data-testid="admin-scope-site-wide"
        title="Not per-property. The property picker has no effect here."
      >
        <span className="h-1.5 w-1.5 rounded-full bg-ink/50" aria-hidden="true" />
        Applies to the whole site
      </p>
    );
  }

  // One home is not a portfolio: offering "All properties" against a single
  // entry is a choice between a thing and itself.
  const showAllOption = properties.length > 1;
  const status = active
    ? `${active.show_on_website ? "On website" : "Hidden from website"} · ${active.booking_enabled ? "Accepting bookings" : "Bookings off"}`
    : isAllProperties && showAllOption
      ? "Every home combined"
      : null;

  // One compact row: the label, the picker and its status, instead of three
  // stacked lines above every page's title.
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
      <label
        htmlFor="admin-property-switcher"
        className="font-sans text-[12px] uppercase tracking-[0.14em] text-ink/70"
      >
        Property
      </label>
      <select
        id="admin-property-switcher"
        className="h-10 min-w-0 flex-1 rounded-md border border-sand-deep bg-white px-3 font-sans text-[15px] text-ink sm:max-w-xs sm:flex-none"
        value={selectedSlug}
        onChange={(event) => setProperty(event.target.value)}
        data-testid="admin-property-switcher"
      >
        {showAllOption && <option value="all">All properties</option>}
        {properties.map((property) => (
          <option key={property.id} value={property.slug || property.id}>
            {property.title}
            {property.publication_status === "published" ? "" : ` (${property.publication_status})`}
          </option>
        ))}
      </select>
      {status && (
        <p className="w-full font-sans text-[12px] text-ink/70 sm:w-auto">{status}</p>
      )}
    </div>
  );
}
